import React, { useState, useEffect, useMemo } from 'react';
import {
  X,
  User,
  Mail,
  Phone,
  Building2,
  Wallet,
  Package,
  CreditCard,
  ExternalLink,
  Search,
  ArrowDownLeft,
  ArrowUpRight,
  ShieldCheck,
  Landmark,
  MapPin,
  Eye,
  RefreshCw,
  Copy,
  Check,
  Download,
  Truck,
  FileSpreadsheet
} from 'lucide-react';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  Base_Url,
  forwardorderlistall,
  shipmentallList,
  b2bAllShipmentList,
  b2bAllShipmentListAll,
  userTrnxList,
  shippingCharges,
  shippingUserCharges,
  Url_For_View_Document
} from '../../config/config';
import { toast } from 'react-toastify';

export const isB2BOrder = (order) => {
  if (!order) return false;

  // 1. Explicit channel or type tag
  const ch = String(order.order_channel || order.order_type || order.shipment_type || order.service_type || '').toUpperCase();
  if (ch.includes('B2B')) return true;
  if (ch.includes('B2C')) return false;

  // 2. Explicit boolean or flag
  if (order.is_b2b === true || order.isB2B === true || order.b2b === true || order.b2b === '1' || order.b2b === 1) {
    return true;
  }

  // 3. Structural B2B characteristics (CFT, total_box, LR number, appointment, partner)
  if (
    (order.cft !== undefined && order.cft !== null && order.cft !== '') ||
    (order.total_cft !== undefined && order.total_cft !== null && order.total_cft !== '') ||
    (order.total_box !== undefined && order.total_box !== null && order.total_box !== '') ||
    order.lrn ||
    order.lr_number ||
    order.appointment_order_id ||
    order.appointment_date ||
    order.partner === 'zoho'
  ) {
    return true;
  }

  return false;
};

export default function CustomerDetailSideDrawer({ isOpen, onClose, customer, isDarkMode }) {
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'orders' | 'wallet'
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingExcel, setDownloadingExcel] = useState(false);
  const [orderSearch, setOrderSearch] = useState('');
  const [orderChannelFilter, setOrderChannelFilter] = useState('all'); // 'all' | 'b2c' | 'b2b'
  const [txnFilter, setTxnFilter] = useState('all'); // 'all' | 'recharges' | 'deductions'
  const [copiedField, setCopiedField] = useState(null);

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Reset tab & fetch data immediately when customer changes
  useEffect(() => {
    if (isOpen && customer?.id) {
      setActiveTab('overview');
      setOrderSearch('');
      setTxnFilter('all');
      setOrders([]); // Clear stale orders immediately so old numbers don't show
      setTransactions([]); // Clear stale transactions immediately
      setLoadingOrders(true);
      setLoadingTransactions(true);

      // Fetch customer orders first, then pass loaded orders into fetchCustomerTransactions
      fetchCustomerOrders(customer.id).then((loadedOrders) => {
        fetchCustomerTransactions(customer.id, loadedOrders || []);
      });
    } else if (!isOpen) {
      setOrders([]);
      setTransactions([]);
    }
  }, [isOpen, customer?.id]);

  // Helper for auth headers (always uses current logged-in user credentials)
  const getAuthHeaders = () => {
    const token = localStorage.getItem('token') || '';
    const cus_id = localStorage.getItem('cus_id') || '';
    return {
      token,
      cus_id,
      'Content-Type': 'application/json',
    };
  };

  // Fetch orders for this customer (Supports B2C & B2B orders)
  const fetchCustomerOrders = async (targetUserId) => {
    if (!targetUserId) return;
    setLoadingOrders(true);
    try {
      const headers = getAuthHeaders();
      if (!headers.token || !headers.cus_id) {
        setLoadingOrders(false);
        return;
      }

      // 1. Read existing local/session cache if available
      const readCachedOrders = () => {
        try {
          const keys = [
            `shipments_${headers.cus_id}`,
            `b2b_shipments_${headers.cus_id}`,
            `b2b_all_shipments_cache_${headers.cus_id}`,
            `shipment_cache_${headers.cus_id}`,
            'all_shipments',
          ];
          const found = [];
          for (const k of keys) {
            const raw = sessionStorage.getItem(k) || localStorage.getItem(k);
            if (raw) {
              const p = JSON.parse(raw);
              if (Array.isArray(p)) {
                const isB2BKey = k.toLowerCase().includes('b2b');
                found.push(
                  ...p.map((it) => ({
                    ...it,
                    order_channel: isB2BKey ? 'B2B' : (it.order_channel || (it.cft || it.total_box ? 'B2B' : 'B2C')),
                  }))
                );
              }
            }
          }
          return found;
        } catch {
          return [];
        }
      };

      // 2. Fetch B2C orders
      const fetchB2C = async () => {
        const results = [];
        try {
          const res = await fetch(`${Base_Url}${shipmentallList}`, { method: 'GET', headers });
          const json = await res.json();
          const items = Array.isArray(json?.data) ? json.data : (Array.isArray(json?.rows) ? json.rows : []);
          if (items.length > 0) results.push(...items.map((o) => ({ ...o, order_channel: 'B2C' })));
        } catch { }
        try {
          const res = await fetch(`${Base_Url}${forwardorderlistall}`, { method: 'GET', headers });
          const json = await res.json();
          const items = Array.isArray(json?.data) ? json.data : (Array.isArray(json?.rows) ? json.rows : []);
          if (items.length > 0) results.push(...items.map((o) => ({ ...o, order_channel: 'B2C' })));
        } catch { }
        return results;
      };

      // 3. Fetch B2B orders
      const fetchB2B = async () => {
        const results = [];
        try {
          const res = await fetch(`${Base_Url}${b2bAllShipmentList}?page=1&limit=100000`, { method: 'GET', headers });
          const json = await res.json();
          const items = Array.isArray(json?.data) ? json.data : (Array.isArray(json?.rows) ? json.rows : []);
          if (items.length > 0) results.push(...items.map((o) => ({ ...o, order_channel: 'B2B' })));
        } catch { }
        try {
          const res = await fetch(`${Base_Url}${b2bAllShipmentListAll}`, { method: 'GET', headers });
          const json = await res.json();
          const items = Array.isArray(json?.data) ? json.data : (Array.isArray(json?.rows) ? json.rows : []);
          if (items.length > 0) results.push(...items.map((o) => ({ ...o, order_channel: 'B2B' })));
        } catch { }
        return results;
      };

      const [cached, b2c, b2b] = await Promise.all([
        Promise.resolve(readCachedOrders()),
        fetchB2C(),
        fetchB2B(),
      ]);

      const combined = [...cached, ...b2c, ...b2b];

      // De-duplicate orders by unique ID / order_id / waybill
      const uniqueMap = new Map();
      combined.forEach((item) => {
        if (!item || typeof item !== 'object') return;
        const key = item.order_id || item.id || item.waybill || item.awb_number;
        if (!key) return;
        const strKey = String(key);
        if (!uniqueMap.has(strKey)) {
          uniqueMap.set(strKey, item);
        } else {
          const existing = uniqueMap.get(strKey);
          const isExistingB2B = existing.order_channel === 'B2B' || isB2BOrder(existing);
          const isItemB2B = item.order_channel === 'B2B' || isB2BOrder(item);
          const channel = isExistingB2B || isItemB2B ? 'B2B' : 'B2C';
          uniqueMap.set(strKey, {
            ...existing,
            ...item,
            order_channel: channel,
          });
        }
      });
      const allOrders = Array.from(uniqueMap.values());

      const tId = String(targetUserId).trim();
      const cEmail = customer?.email ? String(customer.email).trim().toLowerCase() : '';
      const cPhoneDigits = customer?.phone ? String(customer.phone).replace(/\D/g, '').slice(-10) : '';
      const cName = customer?.name ? String(customer.name).trim().toLowerCase() : '';
      const cCompany = customer?.company_name ? String(customer.company_name).trim().toLowerCase() : '';

      const userOrders = allOrders.filter((o) => {
        if (!o || typeof o !== 'object') return false;

        // 1. Direct ID match
        const oUser = String(o.user_id ?? o.retailer_id ?? o.customer_id ?? o.cus_id ?? o.userId ?? '').trim();
        if (oUser && oUser === tId) return true;

        // 2. Email match
        if (cEmail) {
          const email = String(o.email || o.user_email || o.consignee_email || o.pickup_email || '').trim().toLowerCase();
          if (email && (email === cEmail || email.includes(cEmail) || cEmail.includes(email))) return true;
        }

        // 3. Phone match (last 10 digits to ignore country code)
        if (cPhoneDigits) {
          const phones = [o.phone, o.user_phone, o.pickup_phone, o.contact, o.delivery_phone]
            .filter(Boolean)
            .map((p) => String(p).replace(/\D/g, '').slice(-10));
          if (phones.some((p) => p && p === cPhoneDigits)) return true;
        }

        // 4. Pickup Name / Company match
        if (cCompany && cCompany.length > 3) {
          const pickup = String(o.pickup_name || o.pickup_address || o.company_name || '').toLowerCase();
          if (pickup && pickup.includes(cCompany)) return true;
        }
        if (cName && cName.length > 3) {
          const pickup = String(o.pickup_name || '').toLowerCase();
          if (pickup && pickup.includes(cName)) return true;
        }

        return false;
      });

      setOrders(userOrders);
      return userOrders;
    } catch (err) {
      console.error('Failed to fetch customer orders:', err);
      setOrders([]);
      return [];
    } finally {
      setLoadingOrders(false);
    }
  };

  // Fetch wallet transactions (both Top-up Recharges AND Shipment Deductions)
  const fetchCustomerTransactions = async (targetUserId, currentOrders = []) => {
    if (!targetUserId) return;
    setLoadingTransactions(true);
    try {
      const headers = getAuthHeaders();
      if (!headers.token || !headers.cus_id) {
        setLoadingTransactions(false);
        return;
      }

      const tId = String(targetUserId).trim();
      const cEmail = customer?.email ? String(customer.email).trim().toLowerCase() : '';
      const cPhone = customer?.phone ? String(customer.phone).replace(/\D/g, '').slice(-10) : '';

      // 1. Fetch Top-up Recharges (Credits) from userTrnxList
      const fetchTopups = async () => {
        try {
          const res = await fetch(`${Base_Url}${userTrnxList}?page=1&limit=100000`, {
            method: 'GET',
            headers,
          });
          const json = await res.json();
          if (json?.status === 'SUCCESS' && Array.isArray(json.data)) {
            return json.data
              .filter((t) => {
                const tUser = String(t.user_id ?? t.retailer_id ?? t.customer_id ?? t.cus_id ?? '').trim();
                if (tUser && tUser === tId) return true;
                if (cEmail && t.email && String(t.email).trim().toLowerCase() === cEmail) return true;
                if (cPhone && t.phone && String(t.phone).replace(/\D/g, '').slice(-10) === cPhone) return true;
                return false;
              })
              .map((t, idx) => ({
                id: `topup-${t.id || t.trnx_id || idx}`,
                trnx_id: t.trnx_id || t.transaction_id || `TXN-${idx + 1}`,
                order_id: null,
                type: 'credit',
                amount: Number(t.credit_amount || t.amount || 0),
                closing_balance: t.closing_balance || null,
                txn_date: t.txn_date || t.created_at,
                payment_mode: t.payment_mode || 'UPI / Gateway',
                description: 'Wallet Recharge',
                status: t.status === 'add' ? 'Success' : (t.status || 'Success'),
              }));
          }
        } catch (e) {
          console.error('Error fetching top-up history:', e);
        }
        return [];
      };

      // 2. Fetch Shipping Charges / Deductions (Debits) from ledger API
      const fetchLedgerDeductions = async () => {
        const endpoints = [
          `${Base_Url}${shippingUserCharges}?page=1&limit=100000`,
          `${Base_Url}${shippingCharges}?page=1&limit=100000`,
        ];
        for (const url of endpoints) {
          try {
            const res = await fetch(url, { method: 'GET', headers });
            const json = await res.json();
            if (json?.status === 'SUCCESS' && Array.isArray(json.data) && json.data.length > 0) {
              const matched = json.data
                .filter((r) => {
                  const rUser = String(r.user_id ?? r.retailer_id ?? r.customer_id ?? r.cus_id ?? '').trim();
                  if (rUser && rUser === tId) return true;
                  if (cEmail && r.email && String(r.email).trim().toLowerCase() === cEmail) return true;
                  if (cPhone && r.phone && String(r.phone).replace(/\D/g, '').slice(-10) === cPhone) return true;
                  return false;
                })
                .map((r, idx) => {
                  const isCredit = r.status === 'add' || Number(r.credit_amount) > 0;
                  const amt = Number(isCredit ? r.credit_amount : (r.debit_amount || r.amount || 0));
                  return {
                    id: `ledger-${r.id || r.order_id || idx}`,
                    trnx_id: r.order_id ? `ORDER-#${r.order_id}` : (r.trnx_id || `SHIP-${idx + 1}`),
                    order_id: r.order_id || null,
                    type: isCredit ? 'credit' : 'debit',
                    amount: amt,
                    closing_balance: r.closing_balance || null,
                    txn_date: r.txn_date || r.generate_date || r.created_at,
                    payment_mode: 'Wallet Deduction',
                    description: r.txn_description || (r.order_id ? `Shipment Charge • #${r.order_id}` : 'Shipment Freight Debit'),
                    status: r.status || 'Debited',
                  };
                });
              if (matched.length > 0) return matched;
            }
          } catch (e) { }
        }
        return [];
      };

      const [topups, ledgerDebits] = await Promise.all([
        fetchTopups(),
        fetchLedgerDeductions(),
      ]);

      // 3. Fallback: Add shipment deductions directly from customer orders with debit_amount
      // (Guarantees every shipment deduction booked by the customer is always shown)
      const existingOrderIds = new Set(
        ledgerDebits.map((d) => String(d.order_id)).filter(Boolean)
      );

      const orderDebits = (currentOrders || [])
        .filter((o) => {
          const debitAmt = Number(o.debit_amount || o.total_amount || 0);
          return debitAmt > 0 && o.order_id && !existingOrderIds.has(String(o.order_id));
        })
        .map((o) => ({
          id: `order-debit-${o.order_id}`,
          trnx_id: `ORDER-#${o.order_id}`,
          order_id: o.order_id,
          type: 'debit',
          amount: Number(o.debit_amount || o.total_amount || 0),
          closing_balance: null,
          txn_date: o.created_at || o.date,
          payment_mode: 'Shipment Booking',
          description: `Shipment Freight Charge${o.waybill ? ` (AWB: ${o.waybill})` : ''} • ${o.city || 'India'}`,
          status: 'Debited',
        }));

      // Combine and sort chronologically (newest first)
      const allTxns = [...topups, ...ledgerDebits, ...orderDebits].sort((a, b) => {
        const da = a.txn_date ? new Date(a.txn_date).getTime() : 0;
        const db = b.txn_date ? new Date(b.txn_date).getTime() : 0;
        return db - da;
      });

      setTransactions(allTxns);
    } catch (err) {
      console.error('Failed to fetch customer transactions:', err);
      setTransactions([]);
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleCopy = (text, fieldName) => {
    if (!text) return;
    navigator.clipboard.writeText(String(text));
    setCopiedField(fieldName);
    toast.success(`${fieldName} copied!`, { autoClose: 1200 });
    setTimeout(() => setCopiedField(null), 1500);
  };

  const formatBalance = (val) => {
    if (val === undefined || val === null || val === '') return '0.00';
    const n = Number(val);
    return isNaN(n) ? String(val) : n.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return String(dateStr);
      return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return String(dateStr);
    }
  };

  const checkIsActive = (c) => {
    const s = String(c?.status ?? '').trim().toLowerCase();
    const acc = String(c?.account_status ?? '').trim().toLowerCase();
    return s === '1' || s === 'active' || acc === '1' || acc === 'active';
  };

  const { totalRecharges, totalDeductions, rechargeCount, deductionCount } = useMemo(() => {
    let recharges = 0;
    let deductions = 0;
    let rCount = 0;
    let dCount = 0;
    transactions.forEach((t) => {
      const isCredit = t.type === 'credit' || String(t.status).toLowerCase() === 'add' || Number(t.credit_amount) > 0;
      const amt = Number(t.amount || t.credit_amount || t.debit_amount || 0);
      if (isCredit) {
        recharges += amt;
        rCount += 1;
      } else {
        deductions += amt;
        dCount += 1;
      }
    });
    return {
      totalRecharges: recharges,
      totalDeductions: deductions,
      rechargeCount: rCount,
      deductionCount: dCount,
    };
  }, [transactions]);

  const filteredTransactions = useMemo(() => {
    if (txnFilter === 'recharges') {
      return transactions.filter(
        (t) => t.type === 'credit' || String(t.status).toLowerCase() === 'add' || Number(t.credit_amount) > 0
      );
    }
    if (txnFilter === 'deductions') {
      return transactions.filter(
        (t) => !(t.type === 'credit' || String(t.status).toLowerCase() === 'add' || Number(t.credit_amount) > 0)
      );
    }
    return transactions;
  }, [transactions, txnFilter]);

  const { b2bOrderCount, b2cOrderCount } = useMemo(() => {
    let b2b = 0;
    let b2c = 0;
    orders.forEach((o) => {
      if (isB2BOrder(o)) b2b++;
      else b2c++;
    });
    return { b2bOrderCount: b2b, b2cOrderCount: b2c };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let list = orders;
    if (orderChannelFilter === 'b2b') {
      list = list.filter((o) => isB2BOrder(o));
    } else if (orderChannelFilter === 'b2c') {
      list = list.filter((o) => !isB2BOrder(o));
    }
    if (!orderSearch.trim()) return list;
    const q = orderSearch.toLowerCase();
    return list.filter(
      (o) =>
        String(o.order_id || '').toLowerCase().includes(q) ||
        String(o.waybill || o.awb || '').toLowerCase().includes(q) ||
        String(o.city || o.destination || '').toLowerCase().includes(q) ||
        String(o.status || '').toLowerCase().includes(q)
    );
  }, [orders, orderChannelFilter, orderSearch]);

  const downloadTransactionExcel = () => {
    if (!customer) return;
    if (filteredTransactions.length === 0) {
      toast.info('No transactions available to download');
      return;
    }

    setDownloadingExcel(true);
    try {
      const dataRows = filteredTransactions.map((t, idx) => {
        const isCredit =
          t.type === 'credit' ||
          String(t.status).toLowerCase() === 'add' ||
          Number(t.credit_amount) > 0;
        const amt = Number(t.amount || t.credit_amount || t.debit_amount || 0);
        const ref = t.order_id ? `#${t.order_id}` : (t.trnx_id || '—');
        const desc = t.description || (isCredit ? 'Wallet Recharge' : 'Shipment Freight Charge');

        return {
          '#': idx + 1,
          'Date & Time': formatDate(t.txn_date),
          'Order / Reference ID': ref,
          'Transaction ID': t.trnx_id || t.id || '—',
          'Type': isCredit ? 'CREDIT' : 'DEBIT',
          'Amount (₹)': isCredit ? amt : -amt,
          'Closing Balance (₹)': t.closing_balance !== undefined && t.closing_balance !== null && t.closing_balance !== ''
            ? Number(t.closing_balance)
            : '—',
          'Description': desc,
          'Payment Mode': t.payment_mode || '—',
          'Customer ID': customer.id || '—',
          'Customer Name': customer.name || '—',
          'Company': customer.company_name || '—',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataRows);

      // Auto-fit column widths
      worksheet['!cols'] = [
        { wch: 6 },  // #
        { wch: 22 }, // Date & Time
        { wch: 24 }, // Order / Reference ID
        { wch: 22 }, // Transaction ID
        { wch: 12 }, // Type
        { wch: 16 }, // Amount (₹)
        { wch: 20 }, // Closing Balance (₹)
        { wch: 32 }, // Description
        { wch: 16 }, // Payment Mode
        { wch: 14 }, // Customer ID
        { wch: 22 }, // Customer Name
        { wch: 26 }, // Company
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Wallet Activity');

      const now = new Date();
      const safeName = (customer.name || 'Customer').replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Wallet_Statement_${safeName}_#${customer.id || 'log'}_${now.toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, filename);
      toast.success('Wallet statement Excel downloaded successfully!');
    } catch (err) {
      console.error('Failed to generate transaction Excel:', err);
      toast.error('Could not generate Excel. Please try again.');
    } finally {
      setDownloadingExcel(false);
    }
  };

  const downloadOrdersExcel = () => {
    if (!customer) return;
    if (filteredOrders.length === 0) {
      toast.info('No orders available to download');
      return;
    }

    try {
      const dataRows = filteredOrders.map((o, idx) => {
        const isB2B = isB2BOrder(o);
        const orderId = o.order_id || o.id || '—';
        const awb = o.waybill || o.awb || o.lrn || o.lr_number || '—';
        const consignee = o.consignee_name || o.customer_name || o.name || '—';
        const city = o.destination || o.city || o.delivery_city || '—';
        const status = o.status || o.current_status || '—';
        const totalAmt = Number(o.total_amount || o.amount || o.cod_amount || 0);

        return {
          '#': idx + 1,
          'Order ID': orderId,
          'AWB / LR No': awb,
          'Type': isB2B ? 'B2B' : 'B2C',
          'Date': formatDate(o.created_at || o.order_date || o.date),
          'Consignee': consignee,
          'Destination': city,
          'Status': status,
          'Amount (₹)': totalAmt,
          'Boxes / Pkgs': o.total_box || o.boxes || o.package_count || 1,
          'Weight': o.weight ? `${o.weight} kg` : (o.cft ? `${o.cft} CFT` : '—'),
          'Courier Partner': o.partner || o.courier_name || '—',
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(dataRows);
      worksheet['!cols'] = [
        { wch: 6 },
        { wch: 22 },
        { wch: 22 },
        { wch: 10 },
        { wch: 20 },
        { wch: 22 },
        { wch: 18 },
        { wch: 16 },
        { wch: 14 },
        { wch: 14 },
        { wch: 14 },
        { wch: 18 },
      ];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Orders');

      const now = new Date();
      const safeName = (customer.name || 'Customer').replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Orders_${safeName}_#${customer.id || 'all'}_${now.toISOString().split('T')[0]}.xlsx`;

      XLSX.writeFile(workbook, filename);
      toast.success('Orders Excel downloaded successfully!');
    } catch (err) {
      console.error('Failed to export orders to Excel:', err);
      toast.error('Could not export orders. Please try again.');
    }
  };

  const downloadTransactionPdf = async () => {
    if (!customer) return;
    if (filteredTransactions.length === 0) {
      toast.info('No transactions available to download');
      return;
    }

    setDownloadingPdf(true);
    try {
      const DocConstructor = typeof jsPDF === 'function' ? jsPDF : jsPDF.jsPDF;
      const doc = new DocConstructor({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();

      // Top Header Banner
      doc.setFillColor(15, 23, 42); // slate-900
      doc.rect(0, 0, pageWidth, 26, 'F');

      // Brand Title
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(15);
      doc.text('PICKUPXPRESS LOGISTICS', 14, 11);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.setTextColor(148, 163, 184); // slate-400
      doc.text('CUSTOMER WALLET STATEMENT & TRANSACTION LOG', 14, 17);

      // Statement Generation Timestamp
      const now = new Date();
      const genDateStr = now.toLocaleDateString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      });
      const genTimeStr = now.toLocaleTimeString('en-IN', {
        hour: '2-digit',
        minute: '2-digit',
      });
      doc.setFontSize(7.5);
      doc.setTextColor(203, 213, 225); // slate-300
      doc.text(`Generated: ${genDateStr}, ${genTimeStr}`, pageWidth - 14, 11, { align: 'right' });

      const filterLabel =
        txnFilter === 'recharges'
          ? 'Filter: Top-Up Recharges Only'
          : txnFilter === 'deductions'
            ? 'Filter: Shipment Deductions Only'
            : 'Filter: All Activity';
      doc.text(filterLabel, pageWidth - 14, 17, { align: 'right' });

      // Customer Information Card
      doc.setFillColor(248, 250, 252); // slate-50
      doc.setDrawColor(226, 232, 240); // slate-200
      doc.roundedRect(14, 30, pageWidth - 28, 26, 2, 2, 'FD');

      // Left column: Customer Details
      doc.setTextColor(15, 23, 42);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(10);
      doc.text(String(customer.name || 'Unnamed Customer'), 18, 36);

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(71, 85, 105); // slate-600
      doc.text(`Customer ID: #${customer.id || '—'}`, 18, 42);
      doc.text(`Company: ${customer.company_name || 'Individual / Retailer'}`, 18, 48);
      doc.text(`Phone: ${customer.phone || '—'}  |  Email: ${customer.email || '—'}`, 18, 53);

      // Right column: Status & Summary Box
      const currBal = formatBalance(customer.wallet_amount ?? customer.txn_amount ?? 0);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(8.5);
      doc.setTextColor(15, 23, 42);
      doc.text(`Available Balance: INR ${currBal}`, pageWidth - 18, 36, { align: 'right' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(16, 149, 93); // emerald
      doc.text(`Total Recharges: +INR ${formatBalance(totalRecharges)} (${rechargeCount})`, pageWidth - 18, 42, { align: 'right' });

      doc.setTextColor(225, 29, 72); // rose
      doc.text(`Total Deductions: -INR ${formatBalance(totalDeductions)} (${deductionCount})`, pageWidth - 18, 48, { align: 'right' });

      const isCustActive = checkIsActive(customer);
      doc.setTextColor(isCustActive ? 16 : 100, isCustActive ? 149 : 116, isCustActive ? 93 : 139);
      doc.text(`Account Status: ${isCustActive ? 'Active' : 'Inactive'}`, pageWidth - 18, 53, { align: 'right' });

      // Transactions Table Data
      const tableHead = [
        ['#', 'Date & Time', 'Reference / Order', 'Type', 'Description / Mode', 'Amount (INR)', 'Closing Bal']
      ];

      const tableBody = filteredTransactions.map((t, idx) => {
        const isCredit =
          t.type === 'credit' ||
          String(t.status).toLowerCase() === 'add' ||
          Number(t.credit_amount) > 0;
        const amt = Number(t.amount || t.credit_amount || t.debit_amount || 0);
        const sign = isCredit ? '+' : '-';
        const formattedAmt = `${sign} ${amt.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

        const ref = t.order_id ? `#${t.order_id}` : (t.trnx_id || '—');
        const desc = t.description || (isCredit ? 'Wallet Recharge' : 'Shipment Freight Charge');
        const mode = t.payment_mode ? ` [${t.payment_mode}]` : '';

        const closing = t.closing_balance
          ? `${Number(t.closing_balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
          : '—';

        return [
          String(idx + 1),
          formatDate(t.txn_date),
          ref,
          isCredit ? 'CREDIT' : 'DEBIT',
          desc + mode,
          formattedAmt,
          closing,
        ];
      });

      const runAutoTable = (d, opts) => {
        if (typeof autoTable === 'function') {
          autoTable(d, opts);
        } else if (typeof autoTable?.default === 'function') {
          autoTable.default(d, opts);
        } else if (typeof d.autoTable === 'function') {
          d.autoTable(opts);
        }
      };

      runAutoTable(doc, {
        head: tableHead,
        body: tableBody,
        startY: 60,
        margin: { left: 14, right: 14, bottom: 18 },
        theme: 'striped',
        headStyles: {
          fillColor: [15, 23, 42],
          textColor: [255, 255, 255],
          fontSize: 8,
          fontStyle: 'bold',
          halign: 'left',
        },
        alternateRowStyles: {
          fillColor: [248, 250, 252],
        },
        bodyStyles: {
          fontSize: 7.5,
          textColor: [30, 41, 59],
          cellPadding: 2.5,
        },
        columnStyles: {
          0: { cellWidth: 10, halign: 'center' },
          1: { cellWidth: 26 },
          2: { cellWidth: 28 },
          3: { cellWidth: 20, halign: 'center', fontStyle: 'bold' },
          4: { cellWidth: 'auto' },
          5: { cellWidth: 28, halign: 'right', fontStyle: 'bold' },
          6: { cellWidth: 24, halign: 'right' },
        },
        didParseCell: (data) => {
          if (data.section === 'body') {
            const rawRow = filteredTransactions[data.row.index];
            const isCredit =
              rawRow?.type === 'credit' ||
              String(rawRow?.status).toLowerCase() === 'add' ||
              Number(rawRow?.credit_amount) > 0;

            if (data.column.index === 3 || data.column.index === 5) {
              data.cell.styles.textColor = isCredit ? [16, 149, 93] : [225, 29, 72];
            }
          }
        },
        didDrawPage: (data) => {
          const totalPages = doc.internal.getNumberOfPages();
          const curPage = data.pageNumber;
          doc.setFontSize(7.5);
          doc.setTextColor(148, 163, 184);
          doc.setDrawColor(226, 232, 240);
          doc.line(14, pageHeight - 12, pageWidth - 14, pageHeight - 12);
          doc.text(
            'This is a computer-generated statement by PickupXpress Logistics • Confidential',
            14,
            pageHeight - 7
          );
          doc.text(
            `Page ${curPage} of ${totalPages}`,
            pageWidth - 14,
            pageHeight - 7,
            { align: 'right' }
          );
        },
      });

      const safeName = (customer.name || 'Customer').replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Wallet_Statement_${safeName}_#${customer.id || 'log'}_${now.toISOString().split('T')[0]}.pdf`;
      doc.save(filename);
      toast.success('Wallet statement PDF downloaded successfully!');
    } catch (err) {
      console.error('Failed to generate transaction PDF:', err);
      toast.error('Could not generate PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (!isOpen || !customer) return null;

  const isActive = checkIsActive(customer);
  const initials = (customer.name || '?')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join('')
    .toUpperCase();

  const balance = customer.wallet_amount ?? customer.balance ?? 0;

  return (
    <div className="fixed inset-0 z-[9998] flex justify-end transition-opacity duration-300">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 dark:bg-black/70 backdrop-blur-[3px] transition-opacity animate-fadeIn"
        onClick={onClose}
      />

      {/* Slide-over Drawer Panel */}
      <div
        className={`relative z-[9999] h-full w-full sm:w-[560px] md:w-[640px] lg:w-[700px] flex flex-col shadow-2xl rounded-l-xl overflow-hidden transition-transform duration-300 ease-out transform translate-x-0 ${isDarkMode
          ? 'bg-[#0f172a] text-slate-100 border-l border-slate-800'
          : 'bg-white text-slate-800 border-l border-slate-200'
          }`}
        style={{ animation: 'drawerSlideLeft 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}
      >
        <style>{`
          @keyframes drawerSlideLeft {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
        `}</style>

        {/* 1. TOP HEADER & PROFILE CARD */}
        <div
          className={`relative p-5 sm:p-6 border-b shrink-0 overflow-hidden ${isDarkMode
            ? 'bg-gradient-to-br from-slate-900 via-[#0d1424] to-indigo-950/70 border-slate-800'
            : 'bg-gradient-to-br from-violet-50/90 via-white to-sky-50/70 border-slate-200/90'
            }`}
        >
          {/* Subtle background ambient glow */}
          <div className="absolute -top-24 -right-24 w-60 h-60 bg-violet-500/15 dark:bg-violet-500/20 rounded-full blur-3xl pointer-events-none" />
          <div className="absolute -bottom-20 -left-20 w-48 h-48 bg-sky-500/15 dark:bg-indigo-500/20 rounded-full blur-3xl pointer-events-none" />

          {/* Close button */}
          <button
            onClick={onClose}
            className={`absolute top-4 right-4 p-2 rounded-2xl transition-all duration-200 hover:rotate-90 z-20 ${isDarkMode
              ? 'bg-slate-800/80 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700/60 shadow-md'
              : 'bg-white/90 hover:bg-white text-slate-600 hover:text-slate-900 shadow-sm border border-slate-200/80'
              }`}
            title="Close (Esc)"
          >
            <X size={18} />
          </button>

          {/* User profile row */}
          <div className="flex items-start gap-4 pr-12 relative z-10">
            {/* Avatar with luxury ring */}
            <div className="relative shrink-0">
              <div
                className={`w-14 h-14 rounded-2xl flex items-center justify-center text-lg font-black tracking-wider shadow-lg ring-4 ${isDarkMode
                  ? 'bg-gradient-to-tr from-violet-600 via-indigo-600 to-purple-600 text-white ring-slate-800 shadow-indigo-500/25'
                  : 'bg-gradient-to-tr from-violet-600 via-purple-600 to-indigo-500 text-white ring-violet-100 shadow-violet-500/30'
                  }`}
              >
                {initials}
              </div>
              <span
                className={`absolute -bottom-1 -right-1 w-4 h-4 rounded-full border-2 ${isDarkMode ? 'border-slate-900' : 'border-white'
                  } ${isActive ? 'bg-emerald-500 ring-2 ring-emerald-400/40' : 'bg-slate-400'}`}
                title={isActive ? 'Active User' : 'Inactive User'}
              />
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h2 className="text-lg font-black truncate tracking-tight text-slate-900 dark:text-white">
                  {customer.name || 'Unnamed User'}
                </h2>
                <span
                  className={`px-2 py-0.5 rounded-lg text-[10px] font-mono font-black border ${isDarkMode
                    ? 'bg-indigo-500/20 text-indigo-300 border-indigo-500/30'
                    : 'bg-violet-100 text-violet-800 border-violet-200'
                    }`}
                >
                  #{customer.id}
                </span>
                <span
                  className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10.5px] font-bold border ${isActive
                    ? isDarkMode
                      ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30 shadow-xs shadow-emerald-500/10'
                      : 'bg-emerald-100/80 text-emerald-800 border-emerald-200/80 shadow-xs'
                    : isDarkMode
                      ? 'bg-slate-800 text-slate-400 border-slate-700'
                      : 'bg-slate-100 text-slate-500 border-slate-200'
                    }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${isActive ? 'bg-emerald-500 animate-pulse' : 'bg-slate-400'}`} />
                  {isActive ? 'Active' : 'Inactive'}
                </span>
              </div>

              {customer.company_name && (
                <p className={`text-xs flex items-center gap-1.5 mb-2.5 font-medium ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                  <Building2 size={13} className="shrink-0 text-violet-500" />
                  <span className="truncate">{customer.company_name}</span>
                  {customer.company_type && (
                    <span className="text-[10px] px-1.5 py-0.2 rounded bg-slate-200/60 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                      {customer.company_type}
                    </span>
                  )}
                </p>
              )}

              {/* Quick Contact Chips */}
              <div className="flex flex-wrap items-center gap-2 text-xs">
                {customer.email && (
                  <button
                    onClick={() => handleCopy(customer.email, 'Email')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border text-[11px] font-medium transition-all shadow-2xs ${isDarkMode
                      ? 'bg-slate-800/90 border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-slate-300'
                      : 'bg-white/90 border-slate-200 hover:border-slate-300 hover:bg-white text-slate-700'
                      }`}
                    title="Click to copy email"
                  >
                    <Mail size={12} className="text-sky-500 shrink-0" />
                    <span className="truncate max-w-[170px]">{customer.email}</span>
                    {copiedField === 'Email' ? (
                      <Check size={11} className="text-emerald-500" />
                    ) : (
                      <Copy size={11} className="opacity-40" />
                    )}
                  </button>
                )}
                {customer.phone && (
                  <button
                    onClick={() => handleCopy(customer.phone, 'Phone')}
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-xl border text-[11px] font-mono font-medium transition-all shadow-2xs ${isDarkMode
                      ? 'bg-slate-800/90 border-slate-700 hover:border-slate-600 hover:bg-slate-800 text-slate-300'
                      : 'bg-white/90 border-slate-200 hover:border-slate-300 hover:bg-white text-slate-700'
                      }`}
                    title="Click to copy phone"
                  >
                    <Phone size={12} className="text-emerald-500 shrink-0" />
                    <span>{customer.phone}</span>
                    {copiedField === 'Phone' ? (
                      <Check size={11} className="text-emerald-500" />
                    ) : (
                      <Copy size={11} className="opacity-40" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Quick Balance & Metrics Card banner inside header */}
          <div
            className={`mt-4 p-4 rounded-2xl border transition-all duration-200 relative overflow-hidden ${isDarkMode
              ? 'bg-gradient-to-r from-slate-900/95 via-slate-800/90 to-slate-900/95 border-slate-700/80 shadow-lg shadow-black/40'
              : 'bg-gradient-to-r from-emerald-50/60 via-white to-teal-50/50 border-emerald-200/90 shadow-sm'
              }`}
          >
            <div className="flex items-center justify-between gap-4 flex-wrap">
              {/* Left: Wallet Balance */}
              <div className="flex items-center gap-3.5">
                <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white flex items-center justify-center shadow-md shadow-emerald-500/25 shrink-0">
                  <Wallet size={20} />
                </div>
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className={`text-[10px] uppercase font-black tracking-wider ${isDarkMode ? 'text-emerald-400' : 'text-emerald-700'}`}>
                      Available Balance
                    </p>
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                  </div>
                  <p className={`text-xl font-black tracking-tight ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`}>
                    ₹ {formatBalance(balance)}
                  </p>
                </div>
              </div>

              {/* Right: Quick Stat Chips */}
              <div className="flex items-center gap-2 sm:gap-3 text-right">
                <button
                  type="button"
                  onClick={() => setActiveTab('orders')}
                  className={`p-2 rounded-xl border text-center transition-all ${activeTab === 'orders'
                    ? 'border-indigo-500/60 bg-indigo-500/10'
                    : isDarkMode
                      ? 'border-slate-700/70 bg-slate-800/60 hover:bg-slate-800'
                      : 'border-slate-200 bg-white/90 hover:bg-slate-50'
                    }`}
                  title="View Orders"
                >
                  <p className="text-[9.5px] uppercase font-bold tracking-wider text-slate-400">Total Orders</p>
                  <p className="text-sm font-black text-slate-900 dark:text-white flex items-center justify-center gap-1">
                    <Package size={12} className="text-indigo-500" />
                    {loadingOrders ? '...' : orders.length}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('wallet');
                    setTxnFilter('recharges');
                  }}
                  className={`p-2 rounded-xl border text-center transition-all ${activeTab === 'wallet' && txnFilter === 'recharges'
                    ? 'border-emerald-500/60 bg-emerald-500/10'
                    : isDarkMode
                      ? 'border-slate-700/70 bg-slate-800/60 hover:bg-slate-800'
                      : 'border-slate-200 bg-white/90 hover:bg-slate-50'
                    }`}
                  title="View Recharges"
                >
                  <p className="text-[9.5px] uppercase font-bold tracking-wider text-emerald-600 dark:text-emerald-400">Recharges</p>
                  <p className="text-sm font-black text-emerald-600 dark:text-emerald-400 flex items-center justify-center gap-1">
                    <ArrowDownLeft size={12} />
                    {loadingTransactions ? '...' : rechargeCount}
                  </p>
                </button>

                <button
                  type="button"
                  onClick={() => {
                    setActiveTab('wallet');
                    setTxnFilter('deductions');
                  }}
                  className={`p-2 rounded-xl border text-center transition-all ${activeTab === 'wallet' && txnFilter === 'deductions'
                    ? 'border-rose-500/60 bg-rose-500/10'
                    : isDarkMode
                      ? 'border-slate-700/70 bg-slate-800/60 hover:bg-slate-800'
                      : 'border-slate-200 bg-white/90 hover:bg-slate-50'
                    }`}
                  title="View Deductions"
                >
                  <p className="text-[9.5px] uppercase font-bold tracking-wider text-rose-600 dark:text-rose-400">Deductions</p>
                  <p className="text-sm font-black text-rose-600 dark:text-rose-400 flex items-center justify-center gap-1">
                    <ArrowUpRight size={12} />
                    {loadingTransactions ? '...' : deductionCount}
                  </p>
                </button>
              </div>
            </div>
          </div>

          {/* Modern Segmented Navigation Tabs */}
          <div className="flex items-center gap-1.5 p-1.5 mt-4 rounded-2xl bg-slate-200/60 dark:bg-slate-800/80 border border-slate-200/80 dark:border-slate-700/60 backdrop-blur-md">
            {[
              { id: 'overview', label: 'Profile & KYC', icon: User, count: null },
              { id: 'orders', label: 'Orders', icon: Package, count: loadingOrders ? '...' : orders.length },
              { id: 'wallet', label: 'Wallet & History', icon: CreditCard, count: loadingTransactions ? '...' : transactions.length },
            ].map((tab) => {
              const Icon = tab.icon;
              const isSelected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex-1 py-2 px-3 rounded-xl text-xs font-bold flex items-center justify-center gap-2 transition-all duration-200 relative ${isSelected
                    ? 'bg-gradient-to-r from-violet-600 via-indigo-600 to-purple-600 text-white shadow-md shadow-indigo-600/25 scale-[1.01]'
                    : isDarkMode
                      ? 'text-slate-400 hover:text-white hover:bg-slate-700/50'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-white/80'
                    }`}
                >
                  <Icon size={14} className={isSelected ? 'text-white' : 'opacity-70'} />
                  <span>{tab.label}</span>
                  {tab.count !== null && (
                    <span
                      className={`text-[10px] font-mono px-1.5 py-0.2 rounded-full font-bold ${isSelected
                        ? 'bg-white/20 text-white'
                        : isDarkMode
                          ? 'bg-slate-700 text-slate-300'
                          : 'bg-slate-200/80 text-slate-700'
                        }`}
                    >
                      {tab.count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* 2. TAB CONTENT BODY */}
        <div className="flex-1 overflow-y-auto p-5 custom-scrollbar">
          {/* TAB 1: OVERVIEW & PROFILE */}
          {activeTab === 'overview' && (
            <div className="space-y-5">
              {/* Personal & Business Info Section */}
              <div
                className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'
                  }`}
              >
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3.5 flex items-center gap-2 text-violet-500">
                  <User size={14} />
                  Personal & Business Details
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <InfoItem label="Full Name" value={customer.name} isDarkMode={isDarkMode} />
                  <InfoItem label="Customer ID" value={`#${customer.id}`} isDarkMode={isDarkMode} isMono />
                  <InfoItem label="Email Address" value={customer.email} isDarkMode={isDarkMode} />
                  <InfoItem label="Phone Number" value={customer.phone} isDarkMode={isDarkMode} isMono />
                  <InfoItem label="Company Name" value={customer.company_name} isDarkMode={isDarkMode} />
                  <InfoItem label="Company Type" value={customer.company_type} isDarkMode={isDarkMode} />
                  <InfoItem label="Registration Date" value={formatDate(customer.create_date || customer.created_at)} isDarkMode={isDarkMode} />
                  <InfoItem label="Last Login" value={customer.last_login ? formatDate(customer.last_login) : 'Never'} isDarkMode={isDarkMode} />
                  <div className="sm:col-span-2">
                    <InfoItem label="Address" value={customer.address} isDarkMode={isDarkMode} />
                  </div>
                  {customer.website && (
                    <div className="sm:col-span-2">
                      <InfoItem label="Website" value={customer.website} isDarkMode={isDarkMode} isLink />
                    </div>
                  )}
                </div>
              </div>

              {/* KYC Verification & Documents Section */}
              <div
                className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'
                  }`}
              >
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3.5 flex items-center gap-2 text-emerald-500">
                  <ShieldCheck size={14} />
                  KYC Verification & Documents
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <KycDocCard
                    title="Aadhar Card"
                    number={customer.aadhar_number}
                    status={customer.aadhar_status}
                    image={customer.user_address_image}
                    isDarkMode={isDarkMode}
                  />
                  <KycDocCard
                    title="PAN Card"
                    number={customer.pancard_number}
                    status={customer.pan_card_status}
                    image={customer.user_pancard_image}
                    isDarkMode={isDarkMode}
                  />
                  <KycDocCard
                    title="GST Certificate"
                    number={customer.gst_number}
                    status={customer.gst_status}
                    image={customer.user_gst_image}
                    isDarkMode={isDarkMode}
                  />
                </div>
              </div>

              {/* Bank Account Details */}
              <div
                className={`p-4 rounded-2xl border ${isDarkMode ? 'bg-slate-900/60 border-slate-800' : 'bg-slate-50/70 border-slate-200/80'
                  }`}
              >
                <h3 className="text-xs font-bold uppercase tracking-wider mb-3.5 flex items-center gap-2 text-blue-500">
                  <Landmark size={14} />
                  Bank Account Information
                </h3>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
                  <InfoItem label="Bank Name" value={customer.bank_name} isDarkMode={isDarkMode} />
                  <InfoItem label="Account Holder" value={customer.account_holder_name} isDarkMode={isDarkMode} />
                  <InfoItem label="Account Number" value={customer.account_no} isDarkMode={isDarkMode} isMono />
                  <InfoItem label="IFSC Code" value={customer.ifsc_code} isDarkMode={isDarkMode} isMono />
                  {customer.cancel_check_image && (
                    <div className="sm:col-span-2 pt-2">
                      <a
                        href={
                          customer.cancel_check_image.startsWith('http')
                            ? customer.cancel_check_image
                            : `${Url_For_View_Document}${customer.cancel_check_image}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 dark:text-violet-400 hover:underline"
                      >
                        <Eye size={13} />
                        View Cancelled Cheque
                      </a>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: ORDERS LIST */}
          {activeTab === 'orders' && (
            <div className="space-y-4">
              {/* Orders Search & Filter bar */}
              <div className="space-y-2.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      value={orderSearch}
                      onChange={(e) => setOrderSearch(e.target.value)}
                      placeholder="Search by Order ID, AWB, Destination..."
                      className={`w-full pl-9 pr-3 py-2 rounded-xl text-xs border outline-none transition-all ${isDarkMode
                        ? 'bg-slate-800 border-slate-700 text-white focus:border-indigo-500'
                        : 'bg-white border-slate-200 text-slate-800 focus:border-violet-500'
                        }`}
                    />
                  </div>
                  <button
                    onClick={() => fetchCustomerOrders(customer.id)}
                    disabled={loadingOrders}
                    className={`p-2 rounded-xl border text-xs font-semibold flex items-center gap-1.5 ${isDarkMode ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-white border-slate-200 text-slate-600'
                      }`}
                    title="Refresh Orders"
                  >
                    <RefreshCw size={14} className={loadingOrders ? 'animate-spin' : ''} />
                  </button>
                </div>

                {/* Filter Pills & Download Orders Excel */}
                <div className="flex items-center justify-between gap-1.5 flex-nowrap">
                  <div className="flex items-center gap-1 p-0.5 sm:p-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 w-fit shrink-0">
                    <button
                      onClick={() => setOrderChannelFilter('all')}
                      className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10.5px] sm:text-[11px] font-bold transition-all ${orderChannelFilter === 'all'
                        ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs'
                        : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                        }`}
                    >
                      All ({orders.length})
                    </button>
                    <button
                      onClick={() => setOrderChannelFilter('b2c')}
                      className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10.5px] sm:text-[11px] font-bold transition-all flex items-center gap-1 ${orderChannelFilter === 'b2c'
                        ? 'bg-blue-600 text-white shadow-xs'
                        : 'text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30'
                        }`}
                    >
                      <Package size={11} />
                      B2C ({b2cOrderCount})
                    </button>
                    <button
                      onClick={() => setOrderChannelFilter('b2b')}
                      className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10.5px] sm:text-[11px] font-bold transition-all flex items-center gap-1 ${orderChannelFilter === 'b2b'
                        ? 'bg-amber-600 text-white shadow-xs'
                        : 'text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/30'
                        }`}
                    >
                      <Truck size={11} />
                      B2B ({b2bOrderCount})
                    </button>
                  </div>

                  <button
                    onClick={downloadOrdersExcel}
                    disabled={filteredOrders.length === 0}
                    className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1 transition-all shadow-xs shrink-0 ${filteredOrders.length === 0
                      ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700'
                      : 'bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white border-emerald-600 shadow-emerald-500/20 cursor-pointer'
                      }`}
                    title={
                      filteredOrders.length === 0
                        ? 'No orders to download'
                        : `Download Orders Excel (${filteredOrders.length} records)`
                    }
                  >
                    <FileSpreadsheet size={12} />
                    <span>Excel</span>
                  </button>
                </div>
              </div>

              {/* Orders List */}
              {loadingOrders ? (
                <div className="py-16 text-center">
                  <RefreshCw className="w-6 h-6 animate-spin mx-auto text-violet-500 mb-2" />
                  <p className="text-xs text-slate-400">Loading customer orders...</p>
                </div>
              ) : filteredOrders.length === 0 ? (
                <div
                  className={`py-14 text-center rounded-2xl border ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-50 border-slate-200'
                    }`}
                >
                  <Package className="w-10 h-10 mx-auto text-slate-400 mb-2 opacity-50" />
                  <p className="text-xs font-semibold">No orders found</p>
                  <p className="text-[11px] text-slate-400 mt-1">
                    {orderSearch ? 'Try a different search keyword' : 'This customer has not placed any shipments yet.'}
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5">
                  {filteredOrders.map((order, idx) => (
                    <OrderCard key={order.order_id || idx} order={order} isDarkMode={isDarkMode} />
                  ))}
                </div>
              )}
            </div>
          )}

          {/* TAB 3: WALLET & RECHARGES & DEDUCTIONS */}
          {activeTab === 'wallet' && (
            <div className="space-y-4">
              {/* Wallet Summary 3-Card Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                {/* Available Balance */}
                <div
                  className={`p-3.5 rounded-2xl border ${isDarkMode
                    ? 'bg-gradient-to-br from-emerald-950/40 via-slate-900 to-slate-900 border-emerald-500/30'
                    : 'bg-gradient-to-br from-emerald-50 via-white to-white border-emerald-200 shadow-sm'
                    }`}
                >
                  <p className={`text-[9.5px] font-bold uppercase tracking-wider ${isDarkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
                    Available Balance
                  </p>
                  <h3 className="text-lg font-black text-emerald-600 dark:text-emerald-400 tracking-tight mt-0.5">
                    ₹ {formatBalance(balance)}
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5 truncate">
                    Wallet #{customer.id} • {customer.wallet_status || 'Active'}
                  </p>
                </div>

                {/* Total Top-ups */}
                <div
                  className={`p-3.5 rounded-2xl border ${isDarkMode
                    ? 'bg-gradient-to-br from-teal-950/40 via-slate-900 to-slate-900 border-teal-500/30'
                    : 'bg-gradient-to-br from-teal-50 via-white to-white border-teal-200 shadow-sm'
                    }`}
                >
                  <p className={`text-[9.5px] font-bold uppercase tracking-wider flex items-center justify-between ${isDarkMode ? 'text-teal-300' : 'text-teal-700'}`}>
                    <span>Total Added</span>
                    <ArrowDownLeft size={12} />
                  </p>
                  <h3 className="text-lg font-black text-teal-600 dark:text-teal-400 tracking-tight mt-0.5">
                    + ₹ {formatBalance(totalRecharges)}
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {rechargeCount} Recharge{rechargeCount === 1 ? '' : 's'}
                  </p>
                </div>

                {/* Total Deductions (Shipment time debits) */}
                <div
                  className={`p-3.5 rounded-2xl border ${isDarkMode
                    ? 'bg-gradient-to-br from-rose-950/40 via-slate-900 to-slate-900 border-rose-500/30'
                    : 'bg-gradient-to-br from-rose-50 via-white to-white border-rose-200 shadow-sm'
                    }`}
                >
                  <p className={`text-[9.5px] font-bold uppercase tracking-wider flex items-center justify-between ${isDarkMode ? 'text-rose-300' : 'text-rose-700'}`}>
                    <span>Shipment Debits</span>
                    <ArrowUpRight size={12} />
                  </p>
                  <h3 className="text-lg font-black text-rose-600 dark:text-rose-400 tracking-tight mt-0.5">
                    - ₹ {formatBalance(totalDeductions)}
                  </h3>
                  <p className="text-[10px] text-slate-400 mt-0.5">
                    {deductionCount} Deduction{deductionCount === 1 ? '' : 's'}
                  </p>
                </div>
              </div>

              {/* Filter Pills & Refresh */}
              <div className="flex items-center justify-between gap-1.5 flex-nowrap pt-1">
                <div className="flex items-center gap-1 p-0.5 sm:p-1 rounded-xl bg-slate-100 dark:bg-slate-800/80 border border-slate-200 dark:border-slate-700 shrink-0">
                  <button
                    onClick={() => setTxnFilter('all')}
                    className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10.5px] sm:text-[11px] font-bold transition-all ${txnFilter === 'all'
                      ? 'bg-white dark:bg-slate-900 text-slate-900 dark:text-white shadow-xs'
                      : 'text-slate-500 hover:text-slate-800 dark:hover:text-white'
                      }`}
                  >
                    All Activity ({transactions.length})
                  </button>
                  <button
                    onClick={() => setTxnFilter('deductions')}
                    className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10.5px] sm:text-[11px] font-bold transition-all flex items-center gap-1 ${txnFilter === 'deductions'
                      ? 'bg-rose-500 text-white shadow-xs'
                      : 'text-rose-600 dark:text-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/30'
                      }`}
                  >
                    <ArrowUpRight size={11} />
                    Deductions ({deductionCount})
                  </button>
                  <button
                    onClick={() => setTxnFilter('recharges')}
                    className={`px-2 sm:px-2.5 py-1 rounded-lg text-[10.5px] sm:text-[11px] font-bold transition-all flex items-center gap-1 ${txnFilter === 'recharges'
                      ? 'bg-emerald-500 text-white shadow-xs'
                      : 'text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-950/30'
                      }`}
                  >
                    <ArrowDownLeft size={11} />
                    Recharges ({rechargeCount})
                  </button>
                </div>

                <div className="flex items-center gap-1.5 shrink-0">
                  {/* Download Excel */}
                  <button
                    onClick={downloadTransactionExcel}
                    disabled={downloadingExcel || filteredTransactions.length === 0}
                    className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1 transition-all shadow-xs ${filteredTransactions.length === 0
                      ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700'
                      : 'bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white border-emerald-600 shadow-emerald-500/20 cursor-pointer'
                      }`}
                    title={
                      filteredTransactions.length === 0
                        ? 'No transactions to download'
                        : `Download Statement Excel (${filteredTransactions.length} records)`
                    }
                  >
                    <FileSpreadsheet size={12} className={downloadingExcel ? 'animate-bounce' : ''} />
                    <span>{downloadingExcel ? 'Exporting...' : 'Excel'}</span>
                  </button>

                  {/* Download PDF */}
                  <button
                    onClick={downloadTransactionPdf}
                    disabled={downloadingPdf || filteredTransactions.length === 0}
                    className={`px-2.5 py-1 rounded-lg border text-[11px] font-bold flex items-center gap-1 transition-all shadow-xs ${filteredTransactions.length === 0
                      ? 'opacity-50 cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-400 border-slate-200 dark:border-slate-700'
                      : isDarkMode
                        ? 'bg-rose-600/90 hover:bg-rose-600 text-white border-rose-500 shadow-rose-500/20 cursor-pointer'
                        : 'bg-rose-600 hover:bg-rose-700 active:scale-95 text-white border-rose-600 shadow-rose-500/20 cursor-pointer'
                      }`}
                    title={
                      filteredTransactions.length === 0
                        ? 'No transactions to download'
                        : `Download Statement PDF (${filteredTransactions.length} records)`
                    }
                  >
                    <Download size={12} className={downloadingPdf ? 'animate-bounce' : ''} />
                    <span>{downloadingPdf ? 'Generating...' : 'PDF'}</span>
                  </button>

                  {/* Refresh Button */}
                  <button
                    onClick={() => fetchCustomerTransactions(customer.id, orders)}
                    disabled={loadingTransactions}
                    className={`p-1 rounded-lg border text-xs font-semibold flex items-center gap-1 transition-colors ${isDarkMode
                      ? 'bg-slate-800 hover:bg-slate-700 border-slate-700 text-slate-300'
                      : 'bg-white hover:bg-slate-50 border-slate-200 text-slate-600'
                      }`}
                    title="Refresh Wallet Activity"
                  >
                    <RefreshCw size={12} className={loadingTransactions ? 'animate-spin' : ''} />
                  </button>
                </div>
              </div>

              {/* Transactions list */}
              <div>
                {loadingTransactions ? (
                  <div className="py-16 text-center">
                    <RefreshCw className="w-6 h-6 animate-spin mx-auto text-emerald-500 mb-2" />
                    <p className="text-xs text-slate-400">Loading wallet activity & deductions...</p>
                  </div>
                ) : filteredTransactions.length === 0 ? (
                  <div
                    className={`py-12 text-center rounded-2xl border ${isDarkMode ? 'bg-slate-900/40 border-slate-800' : 'bg-slate-50 border-slate-200'
                      }`}
                  >
                    <CreditCard className="w-10 h-10 mx-auto text-slate-400 mb-2 opacity-50" />
                    <p className="text-xs font-semibold">No records found</p>
                    <p className="text-[11px] text-slate-400 mt-1">
                      {txnFilter === 'deductions'
                        ? 'No shipment charges deducted yet for this customer.'
                        : txnFilter === 'recharges'
                          ? 'No top-up recharges found.'
                          : 'No wallet activity recorded.'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {filteredTransactions.map((txn, idx) => (
                      <TransactionCard key={txn.id || txn.trnx_id || idx} txn={txn} isDarkMode={isDarkMode} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* 3. FOOTER */}
        <div
          className={`px-6 py-3.5 border-t flex items-center justify-between text-xs shrink-0 backdrop-blur-md ${isDarkMode
            ? 'bg-slate-900/90 border-slate-800/80 text-slate-400'
            : 'bg-slate-50/90 border-slate-200/80 text-slate-500'
            }`}
        >
          <div className="flex items-center gap-2">
            <span className="text-[11px] font-medium text-slate-400">Account Record:</span>
            <span className="px-2.5 py-0.5 rounded-lg text-[11px] font-mono font-bold bg-slate-200/70 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border border-slate-300/50 dark:border-slate-700/60">
              #{customer.id}
            </span>
          </div>

          {/* <button
            onClick={onClose}
            className={`px-5 py-2 rounded-xl text-xs font-bold transition-all shadow-xs active:scale-95 ${
              isDarkMode
                ? 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                : 'bg-white hover:bg-slate-100 text-slate-800 border border-slate-300/80'
            }`}
          >
            Close Drawer
          </button> */}
        </div>
      </div>
    </div>
  );
}

// ----------------- Helper UI Components -----------------

function InfoItem({ label, value, isDarkMode, isMono = false, isLink = false }) {
  return (
    <div
      className={`p-2.5 rounded-xl border ${isDarkMode ? 'bg-slate-800/40 border-slate-800/80' : 'bg-white border-slate-200/70'
        }`}
    >
      <span className={`block text-[10px] font-bold uppercase tracking-wider mb-0.5 ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
        {label}
      </span>
      {isLink && value ? (
        <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-violet-600 dark:text-violet-400 hover:underline font-medium break-all flex items-center gap-1">
          {value} <ExternalLink size={11} />
        </a>
      ) : (
        <span className={`font-semibold break-all ${isMono ? 'font-mono' : ''} ${value ? '' : 'text-slate-400 font-normal italic'}`}>
          {value || 'Not provided'}
        </span>
      )}
    </div>
  );
}

function KycDocCard({ title, number, status, image, isDarkMode }) {
  const isApproved = String(status) === '1';
  const isPending = String(status) === '0' && image;
  const docUrl = image ? (image.startsWith('http') ? image : `${Url_For_View_Document}${image}`) : null;

  return (
    <div
      className={`p-3 rounded-xl border flex flex-col justify-between gap-2 ${isDarkMode ? 'bg-slate-800/40 border-slate-800' : 'bg-white border-slate-200'
        }`}
    >
      <div>
        <div className="flex items-center justify-between gap-1 mb-1.5">
          <span className="text-[11px] font-bold">{title}</span>
          <span
            className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isApproved
              ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300'
              : isPending
                ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300'
                : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
              }`}
          >
            {isApproved ? 'Approved' : isPending ? 'Pending' : 'Not Uploaded'}
          </span>
        </div>
        <p className="text-[10px] font-mono text-slate-500 dark:text-slate-400 truncate">
          {number || 'No number on file'}
        </p>
      </div>

      {docUrl ? (
        <a
          href={docUrl}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-violet-600 dark:text-violet-400 hover:underline pt-1 border-t border-slate-100 dark:border-slate-800"
        >
          <Eye size={12} /> View File
        </a>
      ) : (
        <span className="text-[10px] text-slate-400 italic pt-1 border-t border-slate-100 dark:border-slate-800">
          No document
        </span>
      )}
    </div>
  );
}

function OrderCard({ order, isDarkMode }) {
  const isB2B = isB2BOrder(order);
  const status = String(order.status || 'Pending').toLowerCase();
  const getStatusBadge = () => {
    if (status.includes('deliver')) return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300 border-emerald-200';
    if (status.includes('transit')) return 'bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-300 border-blue-200';
    if (status.includes('manifest')) return 'bg-purple-100 text-purple-700 dark:bg-purple-500/20 dark:text-purple-300 border-purple-200';
    if (status.includes('cancel')) return 'bg-rose-100 text-rose-700 dark:bg-rose-500/20 dark:text-rose-300 border-rose-200';
    return 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300 border-amber-200';
  };

  let city = order.city || order.destination || order.drop_city || order.pickup_city;
  if (!city && order.dropoff_location) {
    try {
      const drop = typeof order.dropoff_location === 'string' ? JSON.parse(order.dropoff_location) : order.dropoff_location;
      city = drop.city || drop.state;
    } catch { }
  }

  const awb = order.waybill || order.awb || order.awb_number || order.lrn;
  const amount = order.total_amount || order.debit_amount || order.amount || order.cod_amount || 0;
  const product = order.product_desc || order.product_name || order.description || (isB2B ? 'B2B Cargo Shipment' : 'Shipment package');

  return (
    <div
      className={`p-3 rounded-xl border transition-all duration-150 hover:shadow-md ${isDarkMode ? 'bg-slate-800/40 border-slate-800 hover:border-slate-700' : 'bg-white border-slate-200/80 hover:border-slate-300'
        }`}
    >
      <div className="flex items-start justify-between gap-3 mb-1.5">
        <div className="flex items-center gap-2 flex-wrap min-w-0">
          <span className="text-xs font-bold font-mono">#{order.order_id || order.id || '—'}</span>

          {/* B2B / B2C Badge */}
          <span
            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-black tracking-wider uppercase border shadow-2xs ${isB2B
              ? 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30'
              : 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30'
              }`}
            title={isB2B ? 'B2B Cargo Shipment' : 'B2C Express Shipment'}
          >
            {isB2B ? (
              <Truck size={10} className="shrink-0 text-amber-600 dark:text-amber-400" />
            ) : (
              <Package size={10} className="shrink-0 text-blue-600 dark:text-blue-400" />
            )}
            <span>{isB2B ? 'B2B' : 'B2C'}</span>
          </span>

          {awb && (
            <span className="text-[11px] font-mono text-slate-500 dark:text-slate-400">
              AWB: {awb}
            </span>
          )}
        </div>
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border capitalize shrink-0 ${getStatusBadge()}`}>
          {order.status || 'Pending'}
        </span>
      </div>

      <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
        <span className="truncate max-w-[200px]" title={product}>
          {product}
        </span>
        <span className="font-bold text-slate-800 dark:text-slate-200">
          ₹ {Number(amount).toLocaleString('en-IN')}
        </span>
      </div>

      {(city || order.created_at || order.date) && (
        <div className="flex items-center justify-between text-[10px] text-slate-400 mt-1.5 pt-1.5 border-t border-slate-100 dark:border-slate-800">
          <span className="flex items-center gap-1">
            <MapPin size={10} /> {city || 'India'}
          </span>
          <span>{order.created_at || order.date || ''}</span>
        </div>
      )}
    </div>
  );
}

function TransactionCard({ txn, isDarkMode }) {
  const isCredit = txn.type === 'credit' || String(txn.status).toLowerCase() === 'add' || Number(txn.credit_amount) > 0;
  const amount = txn.amount || txn.credit_amount || txn.debit_amount || 0;

  return (
    <div
      className={`p-3 rounded-xl border flex items-center justify-between gap-3 transition-colors ${isDarkMode ? 'bg-slate-800/40 border-slate-800' : 'bg-white border-slate-200/70'
        }`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 font-bold ${isCredit
            ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-500/20 dark:text-emerald-400'
            : 'bg-rose-100 text-rose-600 dark:bg-rose-500/20 dark:text-rose-400'
            }`}
        >
          {isCredit ? <ArrowDownLeft size={16} /> : <ArrowUpRight size={16} />}
        </div>

        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-xs font-bold font-mono truncate">
              {txn.order_id ? `#${txn.order_id}` : (txn.trnx_id || 'Transaction')}
            </p>
            <span
              className={`text-[9.5px] font-bold px-1.5 py-0.2 rounded-md border ${isCredit
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
                : 'bg-rose-50 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300 border-rose-200 dark:border-rose-500/30'
                }`}
            >
              {isCredit ? 'Top-Up' : 'Shipment Debit'}
            </span>
          </div>
          <p className="text-[10.5px] text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mt-0.5 truncate">
            <span>{txn.txn_date || 'Recently'}</span>
            <span>•</span>
            <span className="truncate">{txn.description || txn.payment_mode || (isCredit ? 'Recharge' : 'Shipment Charge')}</span>
          </p>
        </div>
      </div>

      <div className="text-right shrink-0">
        <p className={`text-xs font-extrabold tabular-nums ${isCredit ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
          {isCredit ? '+' : '-'} ₹ {Number(amount).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </p>
        {txn.closing_balance && (
          <p className="text-[10px] text-slate-400 font-mono mt-0.5">
            Bal: ₹ {Number(txn.closing_balance).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
        )}
      </div>
    </div>
  );
}
